import React from "react";
import PropTypes from "prop-types";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { useCheckbox } from "react-aria";

export default function Checkbox({
  name,
  label,
  ariaLabel,
  checked,
  value,
  description,
  required,
  disabled,
  error,
  onChange,
  onBlur,
}) {
  const ref = React.useRef(null);
  const { inputProps } = useCheckbox(
    {
      name,
      "aria-label": ariaLabel || label,
      "aria-describedby": description ? `${name}-description` : undefined,
      isSelected: checked,
      value,
      isRequired: required,
      isDisabled: disabled,
      "aria-errormessage": error ? `${name}-error` : undefined,
      validationState: error ? "invalid" : "valid",
      onChange,
      onBlur,
    },
    {
      isSelected: checked,
      setSelected: onChange,
      toggle: () => (checked ? onChange(false) : onChange(true)),
    },
    ref,
  );

  return (
    <div className="relative flex items-start">
      <div className="flex items-center h-5">
        <input
          ref={ref}
          className={`${
            error
              ? "border-none ring ring-danger focus:ring focus:ring-danger rounded"
              : "focus:ring focus:ring-purple focus:outline-none focus:border-none h-4 w-4 text-purple border-gray-300 rounded"
          }`}
          {...inputProps}
        />
      </div>
      <div className="ml-2 text-sm">
        <label htmlFor={name} className="text-gray-700">
          {label}
        </label>
        {description ? (
          <p id={inputProps["aria-describedby "]} className="text-gray-500">
            {description}
          </p>
        ) : null}
        {error ? (
          <div className="mt-xs flex flex-row gap-xs">
            <ExclamationCircleIcon
              className="h-input-icon text-danger"
              aria-hidden="true"
            />
            <div
              id={inputProps["aria-errormessage"]}
              className="gn-text-sm text-danger"
            >
              {error}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

Checkbox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  ariaLabel: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  checked: PropTypes.bool,
  description: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

Checkbox.defaultProps = {
  ariaLabel: undefined,
  value: undefined,
  required: false,
  disabled: false,
  error: undefined,
  description: undefined,
  checked: false,
  onChange: () => {},
  onBlur: undefined,
};
